import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

import { strings } from '@constants'
import { Spacings, Text } from '@styles'
import { addPdc, editPdc, getBankDetails, uploadPdcChequeImage } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer, toggleFileViewer } from '@data/state/action/root'
import { TOAST_TYPE, UploadButton, DrawerBodyWrapper, DrawerFooterWrapper } from '@base'
import { Button, Calendar, Input, MultiSelect, Select, Textarea } from '@baseV2'

import { InputsWrapper } from '../styles'

const Row = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: ${Spacings.SPACING_3B};
`

const ChequeRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_3B};
`

const ChequeLabel = styled.span`
	font-size: ${Text.SMALL};
	font-weight: 600;
	color: ${({ theme }) => theme.colors.text.primary};
`

const ButtonRow = styled.div`
	display: flex;
	gap: ${Spacings.SPACING_2B};
`

const FooterActions = styled.div`
	display: flex;
	width: 100%;
	gap: ${Spacings.SPACING_3B};
`

const DAY = 60 * 60 * 24 * 1000

const _status = ['MANDATED', 'RECEIVED', 'RELEASED', 'UNCLAIMED', 'USED', 'BOUNCED']
const _type = ['SECURITY', 'TRANSACTION']
const _cheque_type = ['PDC', 'UDC']

const STATUS = _status.map((value) => ({ key: value, label: value }))
const TYPE = _type.map((value) => ({ title: value }))
const CHEQUE_TYPE = _cheque_type.map((value) => ({ key: value, label: value }))

const getFormState = (data) => {
	const _state = {
		cheque_number: { value: null, disabled: false },
		chq_date: { value: new Date().getTime(), disabled: false },
		amount: { value: null, disabled: false },
		bank: { value: {}, disabled: false },
		expiry_date: { value: new Date().getTime() + DAY * 90, disabled: false },
		remarks: { value: null, disabled: false },
		status: { value: STATUS[0], disabled: false },
		type: { value: TYPE[0], disabled: false },
		received_date: { value: null, disabled: false },
		commitment_date: { value: null, disabled: false },
		released_date: { value: null, disabled: false },
		unclaimed_date: { value: null, disabled: false },
		bounce_date: { value: null, disabled: false },
		deposit_date: { value: null, disabled: false },
		po_amount: { value: null, disabled: false },
		cheque_image: { value: '', disabled: false },
		cheque_type: { value: CHEQUE_TYPE[0], disabled: false },
		invoice_numbers: { value: [], disabled: false },
	}

	if (data) {
		_state.cheque_number = { value: data?.cheque_number, disabled: true }
		_state.chq_date = { value: data?.chq_date, disabled: true }
		_state.amount = { value: data?.amount, disabled: true }
		_state.bank = { value: { institution_id: data?.bank, name: data?.bank }, disabled: true }
		if (data?.expiry_date) _state.expiry_date = { value: data?.expiry_date, disabled: true }
		_state.remarks.value = data?.remarks
		_state.status.value = STATUS.find((item) => data?.status === item.key) || {}
		if (data?.type) _state.type = { value: { title: data?.type }, disabled: true }
		if (data?.received_date) _state.received_date = { value: data?.received_date, disabled: true }
		if (data?.commitment_date) _state.commitment_date = { value: data?.commitment_date, disabled: true }
		if (data?.released_date) _state.released_date = { value: data?.released_date, disabled: true }
		if (data?.unclaimed_date) _state.unclaimed_date = { value: data?.unclaimed_date, disabled: true }
		if (data?.bounce_date) _state.bounce_date = { value: data?.bounce_date, disabled: true }
		if (data?.deposit_date) _state.deposit_date = { value: data?.deposit_date, disabled: true }
		if (data?.po_amount) _state.po_amount = { value: data?.po_amount, disabled: true }
		if (data?.cheque_image) _state.cheque_image = { value: data?.cheque_image, disabled: true }
		if (data?.cheque_type) _state.cheque_type = { value: CHEQUE_TYPE.find((item) => item.key === data?.cheque_type), disabled: true }
		if (data?.invoice_numbers) _state.invoice_numbers.value = data?.invoice_numbers.map((item) => ({ title: item }))
	}
	return _state
}

const PdcForm = ({ data, update, organisationId }) => {
	const dispatch = useDispatch()
	const [bankList, setBankList] = useState([])
	const [formState, setFormState] = useState(getFormState())

	useEffect(() => {
		getBankDetails()
			.then((response) => setBankList(response))
			.catch((err) => console.log(err))
	}, [])

	useEffect(() => {
		setFormState(getFormState(data))
	}, [data])

	const updateState = (key) => (value) => setFormState((_s) => ({ ..._s, [key]: { ..._s[key], value } }))

	const chequeDate = new Date(formState.chq_date.value)

	const onChequeDateChange = (value) => {
		updateState('chq_date')(value)
		const _chequeDate = new Date(value)
		updateState('expiry_date')(new Date(_chequeDate.getTime() + DAY * 90).getTime())
	}

	const statusKey = formState.status.value?.key

	const buttonHandler = () => {
		const obj = {
			amount: formState?.amount?.value,
			bank: formState?.bank?.value?.name,
			bounce_date: ['BOUNCED'].includes(statusKey)
				? formState.bounce_date.value
					? new Date(formState.bounce_date.value).getTime()
					: new Date(chequeDate.getTime() + DAY).getTime()
				: null,
			cheque_number: formState?.cheque_number?.value,
			chq_date: formState?.chq_date?.value,
			commitment_date: formState?.commitment_date?.value,
			deposit_date: ['USED'].includes(statusKey)
				? formState.deposit_date.value
					? new Date(formState.deposit_date.value).getTime()
					: new Date(chequeDate.getTime() + DAY).getTime()
				: null,
			expiry_date: formState?.expiry_date?.value,
			po_amount: formState?.po_amount?.value,
			received_date: formState?.received_date?.value,
			released_date: ['RELEASED', 'BOUNCED', 'USED', 'UNCLAIMED'].includes(statusKey)
				? formState.released_date.value
					? new Date(formState.released_date.value).getTime()
					: new Date(chequeDate.getTime() + DAY).getTime()
				: null,
			remarks: formState?.remarks?.value,
			status: statusKey,
			type: formState?.type?.value?.title,
			unclaimed_date: ['UNCLAIMED'].includes(statusKey)
				? formState.unclaimed_date.value
					? new Date(formState.unclaimed_date.value).getTime()
					: new Date(chequeDate.getTime() + DAY).getTime()
				: null,
			cheque_image: formState?.cheque_image?.value,
			cheque_type: formState?.cheque_type?.value?.key,
			invoice_numbers: formState?.invoice_numbers?.value?.map((item) => item?.title),
		}
		dispatch(showLoader(true))
		const apiCall = data ? editPdc(obj, organisationId) : addPdc(obj, organisationId)
		apiCall
			.then(() => {
				dispatch(showToast(true, strings('request_submit_sucess'), { type: TOAST_TYPE.SUCCESS }))
				update()
			})
			.catch((err) => dispatch(showToast(true, err?.message, { type: TOAST_TYPE.ERROR })))
			.finally(() => {
				dispatch(showLoader(false))
				dispatch(toggleDrawer(false))
			})
	}

	const uploadChequeImage = (file) => {
		if (!file) return
		uploadPdcChequeImage(file)
			.then((response) => {
				updateState('cheque_image')(response.file_path)
				dispatch(showToast(true, strings('msg_cheque_image_uploaded_success'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				console.log(error)
				dispatch(showToast(true, strings('msg_cheque_image_uploaded_failed'), { type: TOAST_TYPE.ERROR }))
			})
	}

	const viewChequeImage = () => dispatch(toggleFileViewer(true, { files: formState?.cheque_image?.value }))

	const isDisabled = useMemo(
		() =>
			Object.keys(formState.bank?.value ?? {})?.length === 0 ||
			!formState.cheque_number.value ||
			!formState.amount.value ||
			!formState.chq_date.value,
		[formState]
	)

	const showReleased = ['RELEASED', 'BOUNCED', 'USED', 'UNCLAIMED'].includes(statusKey)

	return (
		<>
			<DrawerBodyWrapper>
				<InputsWrapper>
					<Row>
						<Select
							id='pdc-bank-v2'
							label={strings('select', 'bank')}
							placeholder={strings(['select', 'bank'])}
							data={bankList}
							displayKey='name'
							primaryKey='institution_id'
							value={formState.bank.value ?? {}}
							onChange={updateState('bank')}
							disabled={formState.bank.disabled}
						/>
						<Select
							id='pdc-type-v2'
							label={strings('type')}
							placeholder={strings(['select', 'type'])}
							data={TYPE}
							primaryKey='title'
							displayKey='title'
							value={formState.type.value}
							onChange={updateState('type')}
							disabled={formState.type.disabled}
						/>
					</Row>
					<Row>
						<Input
							id='pdc-cheque-number-v2'
							label={strings('cheque', 'number')}
							placeholder={strings(['enter', 'cheque', 'number'])}
							value={formState.cheque_number.value ?? ''}
							onChange={(e) => updateState('cheque_number')(e.target.value)}
							disabled={formState.cheque_number.disabled}
						/>
						<Input
							id='pdc-amount-v2'
							type='number'
							label={strings('amount')}
							placeholder={strings(['enter', 'amount'])}
							value={formState.amount.value ?? ''}
							onChange={(e) => updateState('amount')(e.target.value)}
							disabled={formState.amount.disabled}
						/>
					</Row>
					<Row>
						<Calendar
							id='pdc-chq-date-v2'
							label={strings('cheque', 'date')}
							value={formState.chq_date.value}
							onChange={onChequeDateChange}
							disabled={formState.chq_date.disabled}
						/>
						<Calendar
							id='pdc-expiry-date-v2'
							label={strings('expiry', 'date')}
							value={formState.expiry_date.value ? new Date(formState.expiry_date.value) : new Date(chequeDate.getTime() + DAY * 90)}
							onChange={updateState('expiry_date')}
							minDate={new Date(chequeDate.getTime() + DAY * 90)}
							disabled={formState.expiry_date.disabled}
						/>
					</Row>
					<Row>
						<Select
							id='pdc-status-v2'
							label={strings('status')}
							placeholder={strings(['select', 'status'])}
							data={STATUS}
							primaryKey='key'
							displayKey='label'
							value={formState.status.value}
							onChange={updateState('status')}
							disabled={formState.status.disabled}
						/>
						<Select
							id='pdc-cheque-type-v2'
							label={strings(['cheque', 'type'])}
							placeholder={strings(['select', 'cheque', 'type'])}
							data={CHEQUE_TYPE}
							primaryKey='key'
							displayKey='label'
							value={formState.cheque_type.value}
							onChange={updateState('cheque_type')}
							disabled={formState.cheque_type.disabled}
						/>
					</Row>

					<Calendar
						id='pdc-commitment-date-v2'
						label={strings(['commitment', 'date'])}
						value={formState.commitment_date.value}
						onChange={updateState('commitment_date')}
						disabled={formState.commitment_date.disabled}
					/>

					{statusKey !== 'MANDATED' && (
						<Calendar
							id='pdc-received-date-v2'
							label={strings(['received', 'date'])}
							value={formState.received_date.value}
							onChange={updateState('received_date')}
							disabled={formState.received_date.disabled}
						/>
					)}

					{showReleased && (
						<Calendar
							id='pdc-released-date-v2'
							label={strings(['released', 'date'])}
							value={formState.released_date.value ? new Date(formState.released_date.value) : new Date(chequeDate.getTime() + DAY)}
							onChange={updateState('released_date')}
							minDate={new Date(chequeDate.getTime() + DAY)}
							disabled={formState.released_date.disabled}
						/>
					)}

					{['UNCLAIMED'].includes(statusKey) && (
						<Calendar
							id='pdc-unclaimed-date-v2'
							label={strings('unclaimed', 'date')}
							value={formState.unclaimed_date.value ? new Date(formState.unclaimed_date.value) : new Date(chequeDate.getTime() + DAY)}
							onChange={updateState('unclaimed_date')}
							minDate={new Date(chequeDate.getTime() + DAY)}
							disabled={formState.unclaimed_date.disabled}
						/>
					)}

					{['BOUNCED'].includes(statusKey) && (
						<Calendar
							id='pdc-bounce-date-v2'
							label={strings(['cheque', 'bounce', 'date'])}
							value={formState.bounce_date.value ? new Date(formState.bounce_date.value) : new Date(chequeDate.getTime() + DAY)}
							onChange={updateState('bounce_date')}
							minDate={new Date(chequeDate.getTime() + DAY)}
							disabled={formState.bounce_date.disabled}
						/>
					)}

					{['USED'].includes(statusKey) && (
						<Calendar
							id='pdc-deposit-date-v2'
							label={strings('deposit', 'date')}
							value={formState.deposit_date.value ? new Date(formState.deposit_date.value) : new Date(chequeDate.getTime() + DAY)}
							onChange={updateState('deposit_date')}
							minDate={new Date(chequeDate.getTime() + DAY)}
							disabled={formState.deposit_date.disabled}
						/>
					)}

					<Input
						id='pdc-po-amount-v2'
						type='number'
						label={strings('po', 'amount')}
						placeholder={strings(['enter', 'po', 'amount'])}
						value={formState.po_amount.value ?? ''}
						onChange={(e) => updateState('po_amount')(e.target.value)}
						disabled={formState.po_amount.disabled}
					/>
					<Textarea
						id='pdc-remarks-v2'
						label={strings('remarks')}
						placeholder={strings(['enter', 'remarks'])}
						value={formState.remarks.value ?? ''}
						onChange={(e) => updateState('remarks')(e.target.value)}
					/>
					<MultiSelect
						id='pdc-invoice-numbers-v2'
						label={strings('invoice_numbers')}
						placeholder={strings(['enter', 'invoice_numbers'])}
						data={[]}
						primaryKey='title'
						displayKey='title'
						value={formState.invoice_numbers.value || []}
						onChange={updateState('invoice_numbers')}
						createNewOption
					/>
					<ChequeRow>
						<ChequeLabel>{strings(['cheque', 'image'])}</ChequeLabel>
						<ButtonRow>
							<Button variant='outline' color='neutral' size='small' disabled={!formState.cheque_image.value} onClick={viewChequeImage}>
								{strings('view')}
							</Button>
							<UploadButton
								xs
								small
								variant='destructive'
								disabled={formState.cheque_image.disabled}
								label={formState.cheque_image?.value ? strings('change') : strings('upload')}
								onChange={uploadChequeImage}
							/>
						</ButtonRow>
					</ChequeRow>
				</InputsWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<FooterActions>
					<Button variant='outline' color='danger' onClick={() => dispatch(toggleDrawer(false))}>
						{strings('cancel')}
					</Button>
					<Button color='primary' disabled={isDisabled} onClick={buttonHandler}>
						{data ? strings('edit') : strings('add')}
					</Button>
				</FooterActions>
			</DrawerFooterWrapper>
		</>
	)
}

export default PdcForm
